
import React, {
  useContext,
  useLayoutEffect,
  useRef,
} from "react";
import { LanguageContext } from "../../shared/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HighLogo from "../components/HighLogo";
import logo from "../../assets/logo.png";

const languages = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "ru", label: "Русский" },
  { code: "tr", label: "Türkçe" },
  { code: "ar", label: "العربية" },
  { code: "hi", label: "हिन्दी" },
];

export default function LanguageSelectorMobile() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const langCtx = useContext(LanguageContext);
  const mainRef = useRef<HTMLElement | null>(null);

  const current = langCtx?.language || i18n.language;

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);

      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, []);

  const handleSelect = (langCode: string) => {
    if (langCtx?.changeLanguage) {
      langCtx.changeLanguage(langCode);
    } else {
      i18n.changeLanguage(langCode);
    }

    navigate(-1);
  };

  return (
    <div style={pageShell}>
      <header style={headerStyle}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={backButtonStyle}
          aria-label={t("common.back", {
            defaultValue: "Back",
          })}
        >
          ←
        </button>

        <button
          type="button"
          onClick={() => navigate("/")}
          style={logoButtonStyle}
          aria-label={t("common.home", {
            defaultValue: "Go to homepage",
          })}
        >
          <HighLogo
            src={logo}
            style={{
              width: 118,
              maxWidth: "34vw",
              display: "block",
            }}
            padding={0}
          />
        </button>

        <div style={headerSpacerStyle} aria-hidden="true" />
      </header>

      <main ref={mainRef} style={mainStyle}>
        <div style={headingWrapStyle}>
          <h1 style={titleStyle}>
            {t(
              "languageSelector.title",
              "Choose your language"
            )}
          </h1>

          <p style={subtitleStyle}>
            {t(
              "languageSelector.subtitle",
              "This changes the app language"
            )}
          </p>
        </div>

        <ul style={languageListStyle}>
          {languages.map((language) => {
            const active =
              current?.toLowerCase().startsWith(language.code) ??
              false;

            return (
              <li key={language.code}>
                <button
                  type="button"
                  onClick={() => handleSelect(language.code)}
                  style={{
                    ...languageButtonStyle,
                    border: active
                      ? "1px solid rgba(163,230,53,0.7)"
                      : "1px solid rgba(148,163,184,0.25)",
                    background: active
                      ? "rgba(163,230,53,0.12)"
                      : "rgba(255,255,255,0.04)",
                    boxShadow: active
                      ? "0 0 0 3px rgba(163,230,53,0.05)"
                      : "none",
                  }}
                  aria-pressed={active}
                >
                  <span style={languageNameWrapStyle}>
                    <span
                      style={{
                        ...statusDotStyle,
                        background: active
                          ? "#a3e635"
                          : "rgba(148,163,184,0.4)",
                        boxShadow: active
                          ? "0 0 12px rgba(163,230,53,0.35)"
                          : "none",
                      }}
                      aria-hidden="true"
                    />

                    <span
                      style={{
                        fontWeight: active ? 700 : 600,
                      }}
                    >
                      {language.label}
                    </span>
                  </span>

                  {active && (
                    <span style={activeTextStyle}>
                      {t(
                        "languageSelector.active",
                        "Active"
                      )}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}

const pageShell: React.CSSProperties = {
  height: "100dvh",
  width: "100%",
  overflow: "hidden",
  background:
    "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#e5e7eb",
  display: "flex",
  flexDirection: "column",
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const headerStyle: React.CSSProperties = {
  position: "relative",
  zIndex: 10,
  flexShrink: 0,
  display: "grid",
  gridTemplateColumns: "44px 1fr 44px",
  alignItems: "center",
  padding:
    "calc(10px + env(safe-area-inset-top)) 16px 10px",
  background: "rgba(2,6,23,0.92)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  borderBottom:
    "1px solid rgba(148,163,184,0.16)",
  boxSizing: "border-box",
};

const backButtonStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  borderRadius: "50%",
  background: "transparent",
  color: "#cbd5e1",
  fontSize: 24,
  cursor: "pointer",
  touchAction: "manipulation",
};

const logoButtonStyle: React.CSSProperties = {
  justifySelf: "center",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  touchAction: "manipulation",
};

const headerSpacerStyle: React.CSSProperties = {
  width: 44,
  height: 44,
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
  padding:
    "24px 20px calc(28px + env(safe-area-inset-bottom))",
  boxSizing: "border-box",
  scrollBehavior: "auto",
};

const headingWrapStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 480,
  margin: "0 auto 22px",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  color: "#f8e298",
  fontSize: 27,
  lineHeight: 1.12,
  letterSpacing: "-0.8px",
  fontWeight: 800,
};

const subtitleStyle: React.CSSProperties = {
  margin: "7px 0 0",
  color: "#94a3b8",
  fontSize: 13,
  lineHeight: 1.5,
};

const languageListStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 480,
  listStyle: "none",
  padding: 0,
  margin: "0 auto",
  display: "grid",
  gap: 12,
};

const languageButtonStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 58,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "14px 16px",
  borderRadius: 16,
  color: "#e5e7eb",
  fontSize: 16,
  textAlign: "left",
  cursor: "pointer",
  boxSizing: "border-box",
  transition:
    "border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease",
  touchAction: "manipulation",
};

const languageNameWrapStyle: React.CSSProperties = {
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const statusDotStyle: React.CSSProperties = {
  width: 12,
  height: 12,
  flexShrink: 0,
  borderRadius: 999,
};

const activeTextStyle: React.CSSProperties = {
  flexShrink: 0,
  color: "#a3e635",
  fontSize: 12,
  fontWeight: 700,
};

